/**
 * Ranking raid attackers.
 *
 * THESE ARE OUR NUMBERS, not someone else's published ones. That is the honest
 * difference between the two halves of the Meta page: PvP defers to PvPoke's
 * rankings, and raids have no equivalent published feed, so the app computes
 * them from the game's own data. Everything here is therefore stated rather
 * than assumed — the formulas below are the ones the UI cites.
 *
 * WHY THE RANKING IS COMPUTED HERE AND NOT BAKED. A raid ranking only means
 * something against a target: which of an attacker's moves you use, and how
 * hard it lands, both depend on the boss. Pre-computing eighteen rankings
 * would be eighteen times the payload for a fraction of the questions, so the
 * generated file ships the INPUTS (stats, move pools, the type chart) and this
 * ranks them on demand. A full pass is a few thousand move-pair evaluations —
 * cheap enough to run on every filter change.
 *
 * THE MOVESET IS CHOSEN PER MEASURE. The best DPS pair and the best ER pair
 * are not always the same moveset, and quoting one while ranking by the other
 * would recommend a build that does not produce the number beside it.
 */
import {
  RAID_ATTACKERS,
  RAID_EVAL_CPM,
  RAID_MOVES,
  RAID_SHADOW_ATTACK_MULT,
  RAID_SHADOW_DAMAGE_TAKEN_MULT,
  RAID_TYPE_CHART,
  RAID_TYPE_ORDER,
  RAID_EVAL_IV,
  type RaidAttacker,
  type RaidMove,
} from "./generated/raidMeta.generated";

export type { RaidAttacker, RaidMove };
export { RAID_TYPE_ORDER };

/**
 * The reference defender every attacker is measured against.
 *
 * A constant scales every attacker identically, so it cannot change the ORDER
 * — it is here so the numbers are recognisable rather than arbitrary. 180 is a
 * tier-5 boss's defence, which is what "raid DPS" means to anyone who has read
 * a counter list.
 */
const REF_DEFENCE = 180;

/**
 * Incoming damage per second from a reference boss, used for survival time.
 *
 * Same argument: it scales TDO uniformly. Roughly a tier-5 boss's output
 * against a neutral defender.
 */
const REF_INCOMING_DPS = 900;

/** Same-type attack bonus. */
const STAB = 1.2;

/** The measures a trainer can rank by. */
export const RAID_MEASURES = [
  {
    key: "er",
    label: "Rating",
    blurb:
      "The balanced pick: damage weighted against staying alive. 100 is the best you could bring.",
  },
  {
    key: "dps",
    label: "DPS",
    blurb: "Raw damage per second. Glass cannons win this one.",
  },
  {
    key: "tdo",
    label: "TDO",
    blurb: "Total damage before fainting. Bulk over burst.",
  },
  {
    key: "attack",
    label: "Attack",
    blurb: "Effective attack stat alone, before any move is considered.",
  },
  {
    key: "defence",
    label: "Defence",
    blurb: "Effective defence stat alone. Nothing to do with damage dealt.",
  },
  {
    key: "stamina",
    label: "HP",
    blurb: "Effective stamina alone. Nothing to do with damage dealt.",
  },
] as const;

export type RaidMeasureKey = (typeof RAID_MEASURES)[number]["key"];

export function raidMeasureByKey(key: string) {
  return RAID_MEASURES.find((m) => m.key === key) ?? RAID_MEASURES[0];
}

/**
 * How hard `moveType` lands on a boss with `bossTypes`. 1 when no boss is set.
 *
 * A real boss usually has TWO types and the multipliers compound, which is
 * most of why "counters for this boss" beats "counters for this type":
 * Ice into Giratina (Ghost/Dragon) is 1.6, but Ice into Garchomp
 * (Dragon/Ground) is 1.6 x 1.6 = 2.56, and that gap reorders the whole list.
 */
export function effectiveness(moveType: string, bossTypes: readonly string[]): number {
  const row = RAID_TYPE_CHART[moveType];
  if (!row) return 1;
  let mult = 1;
  for (const t of bossTypes) {
    const col = RAID_TYPE_ORDER.indexOf(t);
    if (col >= 0) mult *= row[col];
  }
  return mult;
}

/** Effective stats at the evaluated level, shadow bonuses applied. */
function effectiveStats(a: RaidAttacker, shadow: boolean) {
  const atk = (a.atk + RAID_EVAL_IV) * RAID_EVAL_CPM * (shadow ? RAID_SHADOW_ATTACK_MULT : 1);
  // A shadow does not have less defence; it TAKES more damage. Dividing here
  // is the same arithmetic for survival time and keeps one number to carry,
  // but it is why the Defence column shows the undivided stat.
  const rawDef = (a.def + RAID_EVAL_IV) * RAID_EVAL_CPM;
  const def = shadow ? rawDef / RAID_SHADOW_DAMAGE_TAKEN_MULT : rawDef;
  const hp = Math.floor((a.sta + RAID_EVAL_IV) * RAID_EVAL_CPM);
  return { atk, def, rawDef, hp };
}

/** Damage one use of a move deals to the reference defender. */
function moveDamage(
  move: RaidMove,
  atk: number,
  types: string[],
  bossTypes: readonly string[],
): number {
  const stab = types.includes(move.type) ? STAB : 1;
  return (
    0.5 * move.power * (atk / REF_DEFENCE) * stab * effectiveness(move.type, bossTypes) + 1
  );
}

export interface RaidRanked {
  /** 1-based position in the current ranking. */
  rank: number;
  attacker: RaidAttacker;
  shadow: boolean;
  /** Display name, with the form and Shadow folded in. */
  name: string;
  dps: number;
  tdo: number;
  er: number;
  attack: number;
  defence: number;
  stamina: number;
  fast: RaidMove | null;
  charged: RaidMove | null;
  /** The value of the measure this list was ranked by. */
  value: number;
}

/**
 * One attacker's best line against a boss type, for one measure.
 *
 * The cycle model: firing a charged move costs energy that only fast moves
 * generate, so the honest unit is a whole cycle — n fast moves and one charged
 * move — rather than either move in isolation. Quoting a charged move's damage
 * per second alone is the classic way to rank a move nobody can afford to use.
 */
function evaluate(
  a: RaidAttacker,
  shadow: boolean,
  bossTypes: readonly string[],
  measure: RaidMeasureKey,
): RaidRanked {
  const { atk, def, rawDef, hp } = effectiveStats(a, shadow);
  const survival = (hp * def) / REF_INCOMING_DPS;

  let best: { dps: number; fast: RaidMove | null; charged: RaidMove | null } = {
    dps: 0,
    fast: null,
    charged: null,
  };
  let bestScore = -Infinity;

  for (const fi of a.fast) {
    const fast = RAID_MOVES[fi];
    if (!fast || fast.durationMs <= 0 || fast.energy <= 0) continue;
    const fastDmg = moveDamage(fast, atk, a.types, bossTypes);
    const fastSec = fast.durationMs / 1000;

    for (const ci of a.charged) {
      const charged = RAID_MOVES[ci];
      if (!charged || charged.durationMs <= 0 || charged.energy >= 0) continue;
      const cost = Math.abs(charged.energy);
      const n = Math.ceil(cost / fast.energy);
      const cycleTime = n * fastSec + charged.durationMs / 1000;
      if (cycleTime <= 0) continue;
      const cycleDmg = n * fastDmg + moveDamage(charged, atk, a.types, bossTypes);
      const dps = cycleDmg / cycleTime;

      // Score by the measure being ranked, so the moveset quoted is the one
      // that produces the number beside it.
      const tdo = dps * survival;
      const score =
        measure === "tdo" ? tdo : measure === "dps" ? dps : Math.pow(dps, 3) * tdo;
      if (score > bestScore) {
        bestScore = score;
        best = { dps, fast, charged };
      }
    }
  }

  const dps = best.dps;
  const tdo = dps * survival;
  // The community's balanced metric: damage weighted three-to-one against
  // staying alive, then brought back to a damage-like scale.
  const er = dps > 0 ? Math.pow(Math.pow(dps, 3) * tdo, 0.25) : 0;

  const name = `${a.name}${a.form ? ` (${a.form})` : ""}${shadow ? " (Shadow)" : ""}`;
  const value =
    measure === "dps"
      ? dps
      : measure === "tdo"
        ? tdo
        : measure === "er"
          ? er
          : measure === "attack"
            ? atk
            : measure === "defence"
              ? rawDef
              : hp;

  return {
    rank: 0,
    attacker: a,
    shadow,
    name,
    dps,
    tdo,
    er,
    attack: atk,
    defence: rawDef,
    stamina: hp,
    fast: best.fast,
    charged: best.charged,
    value,
  };
}

export interface RaidFilters {
  /**
   * The types the BOSS has. Empty for a neutral ranking, one entry when a type
   * was picked by hand, two when a real boss with dual typing was picked.
   */
  bossTypes: string[];
  /**
   * The boss's NAME when the target came from the live roster rather than the
   * type chips. Display only — the ranking runs off `bossTypes` either way —
   * but "counters for Mega Garchomp" is a different sentence to "counters for
   * a Dragon boss", and the page should be able to say which one it means.
   */
  bossLabel: string | null;
  /** A type key the ATTACKER must have, or "all". */
  attackerType: string;
  /** Include the Shadow form of anything that has one. */
  includeShadow: boolean;
}

export const NO_RAID_FILTERS: RaidFilters = {
  bossTypes: [],
  bossLabel: null,
  attackerType: "all",
  includeShadow: true,
};

/**
 * The ranked attackers, best first.
 *
 * `limit` exists because the honest roster is over a thousand entries and
 * nobody reads past the first screen; the count is reported so the list never
 * pretends to be the whole thing.
 */
export function rankRaidAttackers(
  filters: RaidFilters,
  measure: RaidMeasureKey,
  limit = 100,
): { rows: RaidRanked[]; total: number } {
  const bossTypes = filters.bossTypes;
  const out: RaidRanked[] = [];

  for (const a of RAID_ATTACKERS) {
    if (filters.attackerType !== "all" && !a.types.includes(filters.attackerType)) continue;
    out.push(evaluate(a, false, bossTypes, measure));
    if (filters.includeShadow && a.shadow) {
      // Shadows are ranked as their own row, not a footnote: the +20% attack
      // trade is strongly positive in raids, which is why real counter lists
      // are full of them.
      out.push(evaluate(a, true, bossTypes, measure));
    }
  }

  out.sort((x, y) => y.value - x.value);

  // RATING IS NORMALISED SO THE BEST AVAILABLE READS 100.
  //
  // Raw ER is (DPS^3 * TDO)^0.25, a damage-scaled quantity with no ceiling, so
  // it happily prints 100.8 -- arithmetically fine, and unreadable next to the
  // PvP tab where the score genuinely IS out of 100. Anyone seeing "Rating
  // 100.8" concludes the page is broken, which is a worse outcome than losing
  // the absolute figure nobody was using.
  //
  // Scaled to the top of THIS ranking, so it answers the question a counter
  // list is actually asked: how close is this to the best thing I could bring
  // against this target? The order is already fixed by the sort above, so
  // scaling cannot change it.
  //
  // Only Rating is scaled. DPS, TDO, Attack, Defence and HP are real units and
  // are left alone.
  const topValue = out[0]?.value ?? 0;
  const scale = measure === "er" && topValue > 0 ? 100 / topValue : 1;

  const rows = out
    .slice(0, limit)
    .map((r, i) => ({ ...r, rank: i + 1, value: r.value * scale }));
  return { rows, total: out.length };
}

/** Types an attacker can have, ordered by how many carry them. */
export function attackerTypes(): string[] {
  const counts = new Map<string, number>();
  for (const a of RAID_ATTACKERS) {
    for (const t of a.types) counts.set(t, (counts.get(t) ?? 0) + 1);
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([t]) => t);
}

/**
 * The reference numbers the cycle model runs on, for the Rocket counters
 * engine (lib/rocketCounters), which ranks the trainer's OWN Pokémon with
 * the same arithmetic so its numbers sit on the Battle Hub's scale.
 */
export const RAID_REF_DEFENCE = REF_DEFENCE;
export const RAID_REF_INCOMING_DPS = REF_INCOMING_DPS;
export const RAID_STAB = STAB;
